import { useEffect, useState } from 'react';

import { useUser } from '@clerk/react';

import {
  DEFAULT_TASK_GROUP_POLL_INTERVAL_MS,
  DEFAULT_TASK_GROUP_POLL_TIMEOUT_MS,
} from './constants';
import { useGetUserPreferences } from '../../services/user.service';

interface UseDefaultTaskGroupIdReturn {
  defaultTaskGroupId: string | null;
  isResolving: boolean;
}

export function useDefaultTaskGroupId(): UseDefaultTaskGroupIdReturn {
  const { user, isLoaded: isClerkUserLoaded } = useUser();
  const clerkDefaultTaskGroupId =
    (user?.unsafeMetadata?.defaultTaskGroupId as string) ?? null;

  const [serverDefaultTaskGroupId, setServerDefaultTaskGroupId] = useState<
    string | null
  >(null);
  const [hasExhaustedRetries, setHasExhaustedRetries] = useState(false);

  // Clerk's unsafeMetadata copy of defaultTaskGroupId, and the server's own
  // User.defaultTaskGroupId that getUserPreferences reads, are both written
  // by the same user.created SQS webhook (processUserCreated). A client that
  // reaches DoBox before that webhook lands (fast no-email onboarding skips
  // the OAuth round trip that normally buys it enough time) can see both
  // sources empty on the first read. Retry the server fallback on a bounded
  // window, mirroring the retry budget useCreateWelcomeDoBox already spends
  // on this identical race, instead of concluding there is no default after
  // one unlucky attempt. This narrows the race, it does not close it — a
  // sufficiently slow webhook (SQS backlog, cold start, a retry) still loses.
  // The durable fix is a lazy create-if-missing on the server read path; see
  // the PR description / handoff notes.
  const skipServerFallback =
    !isClerkUserLoaded ||
    Boolean(clerkDefaultTaskGroupId) ||
    Boolean(serverDefaultTaskGroupId) ||
    hasExhaustedRetries;

  const { userPreferences } = useGetUserPreferences({
    skip: skipServerFallback,
    pollInterval: skipServerFallback
      ? undefined
      : DEFAULT_TASK_GROUP_POLL_INTERVAL_MS,
  });

  useEffect(() => {
    if (userPreferences?.defaultTaskGroupId) {
      setServerDefaultTaskGroupId(userPreferences.defaultTaskGroupId);
    }
  }, [userPreferences?.defaultTaskGroupId]);

  useEffect(() => {
    if (skipServerFallback) {
      return;
    }
    const timeoutId = setTimeout(() => {
      setHasExhaustedRetries(true);
    }, DEFAULT_TASK_GROUP_POLL_TIMEOUT_MS);
    return () => clearTimeout(timeoutId);
  }, [skipServerFallback]);

  const defaultTaskGroupId =
    clerkDefaultTaskGroupId ?? serverDefaultTaskGroupId ?? null;

  // Reduces to: not resolved yet (Clerk, then the server fallback) and the
  // retry window isn't exhausted. skipServerFallback is always false here —
  // it would only be true via one of the three conditions this branch has
  // already ruled out — so there is no separate "still fetching" check to add.
  const isResolving =
    !isClerkUserLoaded ||
    (!clerkDefaultTaskGroupId &&
      !serverDefaultTaskGroupId &&
      !hasExhaustedRetries);

  return { defaultTaskGroupId, isResolving };
}
